import { FormsModule } from '@angular/forms';
import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { AppIconComponent } from '../../common/components/icon/icon.component';
import getSafeIconName from '../../common/functions/get-safe-icon-name';
import { getCategoryLabel } from '../../common/utils/insurance-category.util';
import { RouterModule } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { OptionModalComponent } from '../insurance-types/ui/components/option-modal/option-modal.component';
import { DownloadQuoteModalComponent } from '../insurance-types/ui/components/download-quote-modal/download-quote-modal.component';
import { ActivatedRoute, Router } from '@angular/router';
import { CoverageAmountInputComponent } from '../../common/components/coverage-amount-input/coverage-amount-input.component';
import { ReactiveFormsModule, FormControl, Validators } from '@angular/forms';
import { QuotesService } from '../../core/data/services/quotes.service';
import { QuoteSummaryDto } from '../../core/data/dtos/quote-summary.dto';
import { QuoteDetailDto } from '../../core/data/dtos/quote-detail.dto';
import { InsuranceTypeCoverageDto } from '../../core/data/dtos/insurance-type-coverage.dto';
import { InsuranceTypeCurrencyDto } from '../../core/data/dtos/insurance-type-currency.dto';
import { DecimalPipe } from '@angular/common';
import { lastValueFrom } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { SwalService } from '../../common/services/swal.service';
import { NgxMaskDirective } from 'ngx-mask';
import {
  matchCoverage,
  resolveSelectedPlanId,
  mapCoveragesFromForm,
  mapInsuredPeopleFromForm,
  mapAnswersFromForm,
  normalizeProducerCode,
} from '../../common/utils/quote-detail-mappers';
import { formatDescription } from '../../common/utils/format-description';
import { calcAgeYears } from '../../common/utils/date-utils';

type PopoverCtor = typeof import('bootstrap/js/dist/popover.js').default;
type PopoverInstance = import('bootstrap/js/dist/popover.js').default;

@Component({
  selector: 'app-summary',
  standalone: true,
  imports: [AppIconComponent, RouterModule, DecimalPipe, NgxMaskDirective, FormsModule, CoverageAmountInputComponent, ReactiveFormsModule],
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.css'],
})
export class SummaryComponent
  implements AfterViewInit, AfterViewChecked, OnDestroy {
  swal = inject(SwalService);
  get hasErrors(): boolean {
    return this.isAnyControlInvalid();
  }

  readonly getCategoryLabel = getCategoryLabel;
  get currentDetails(): QuoteDetailDto[] {
    return this.quotes.currentQuote?.details ?? [];
  }

  get paymentPlanLabel(): string {
    switch (this.paymentsPlan) {
      case 1:
        return 'anual';
      case 4:
        return 'trimestral';
      case 6:
        return 'semestral';
      case 12:
        return 'mensual';
      default:
        return 'anual';
    }
  }

  get quoteCurrency(): string {
    const details = this.currentDetails;
    if (details.length > 0 && details[0]?.currency) {
      const cur = details[0].currency.toString().trim().toUpperCase();
      return cur === 'USD' ? 'US$' : 'RD$';
    }
    return 'RD$';
  }

  detailNetPremium(detailId?: number): number {
    if (!detailId || !this.summary?.details) return 0;
    const d = this.summary.details.find((x) => x.detailId === detailId);
    return d?.netPremium ?? 0;
  }

  getNonRequiredCoverages(detail: QuoteDetailDto): InsuranceTypeCoverageDto[] {
    const all = detail.insuranceType?.coverages ?? [];
    // Filter out required/primary coverages and calculated coverages
    const required = this.getRequiredCoverages(detail);
    return all.filter((c) => !required.includes(c) && !c.isCalculated);
  }

  getRequiredCoverages(detail: QuoteDetailDto): InsuranceTypeCoverageDto[] {
    return (detail.insuranceType?.coverages ?? []).filter(
      (c) => !!c.isRequired
    );
  }

  isEnfermedadesGraves(detail: QuoteDetailDto): boolean {
    return detail.insuranceType?.formulaId === 'EnfermedadesGraves';
  }

  hasExpandableContent(detail: QuoteDetailDto): boolean {
    const nonRequired = this.getNonRequiredCoverages(detail);
    const benefitsCount = detail.insuranceType?.benefits?.length ?? 0;
    return nonRequired.length > 0 || benefitsCount > 0;
  }

  /**
   * Gets the calculated survival benefit amount from the quote summary breakdown.
   * Returns the value of 'MontoAsegurado:Supervivencia' from the breakdown.
   */
  getSurvivalBenefit(detailId?: number): number | null {
    if (!detailId || !this.summary?.details) return null;
    const d = this.summary.details.find((x) => x.detailId === detailId);
    return (d?.breakdown?.['MontoAsegurado:Supervivencia'] as number) ?? null;
  }

  /**
   * Check if the detail has a calculated coverage (like Supervivencia).
   */
  hasCalculatedCoverage(detail: QuoteDetailDto): boolean {
    const all = detail.insuranceType?.coverages ?? [];
    return all.some((c) => !!c.isCalculated);
  }

  /**
   * Calculates the primary insured's age in whole years from their birth date.
   * Returns null if no primary insured or no birth date is available.
   */
  getPrimaryAge(detail: QuoteDetailDto): number | null {
    const primary = (detail.insuredPeople ?? []).find((ip) => ip.isPrimary);
    // Handles both ISO (from the API) and dd/mm/yyyy (from the edit modal) as local dates.
    return calcAgeYears(primary?.person?.birthDate ?? null);
  }

  /**
   * Returns true when the coverage should be disabled because the primary
   * insured's age violates the coverage's maxAge constraint.
   * Mirrors the logic in option-modal's applyCoverageAgeConstraints().
   */
  isCoverageDisabledByAge(
    detail: QuoteDetailDto,
    cov: InsuranceTypeCoverageDto,
  ): boolean {
    const age = this.getPrimaryAge(detail);
    return age != null && typeof cov.maxAge === 'number' && age > cov.maxAge;
  }

  isCoverageSelected(
    detail: QuoteDetailDto,
    cov: InsuranceTypeCoverageDto,
  ): boolean {
    const selected = detail.coverages ?? [];
    return !!selected.find((s) => matchCoverage(s, cov));
  }

  getCoverageAmount(
    detail: QuoteDetailDto,
    cov: InsuranceTypeCoverageDto,
  ): number | null {
    const selected = detail.coverages ?? [];
    const found = selected.find((s) => matchCoverage(s, cov));
    return found?.amount ?? null;
  }

  onToggleCoverage(
    detail: QuoteDetailDto,
    cov: InsuranceTypeCoverageDto,
    checked: boolean,
  ): void {
    const cur = this.quotes.currentQuote;
    if (!cur || !detail.id) return;

    // Never allow selecting a coverage the primary insured is too old for.
    if (checked && this.isCoverageDisabledByAge(detail, cov)) return;

    // Determine the correct initial amount for the coverage BEFORE saving
    const range = this.getCoverageRange(detail, cov);
    let initialAmount = range?.minAmount ?? 0;

    if (checked) {
      const isFixed = this.isCoverageFixedLimit(detail, cov);
      const isFraction = this.hasFraction(cov);

      if (isFraction) {
        const fraction = cov.insurePrimaryFraction ?? 0;
        const requiredTotal = this.getRequiredCoveragesTotal(detail);
        initialAmount = Math.round((fraction / 100) * requiredTotal * 100) / 100;
      } else if (isFixed && range?.maxAmount !== undefined) {
        initialAmount = range.maxAmount;
      } else if (initialAmount <= 0 && range?.maxAmount !== undefined) {
        // If no minimum is configured, use maxAmount as default for the coverage
        initialAmount = range.maxAmount;
      }
    }

    const details = (cur.details ?? []).map((d) => {
      if (d.id !== detail.id) return d;
      const existing = d.coverages ?? [];
      const idx = existing.findIndex((s) => matchCoverage(s, cov));
      if (checked) {
        if (idx === -1) {
          existing.push({
            coverage: cov,
            coverageId: cov.id ?? null,
            coverageCode: cov.coverageCode ?? null,
            name: cov.name ?? null,
            description: cov.description ?? null,
            amount: initialAmount,
          });
        }
      } else {
        if (idx !== -1) existing.splice(idx, 1);
      }
      return { ...d, coverages: [...existing] };
    });
    this.quotes.setCurrentQuoteLocal({ ...cur, details });

    // Update control state BEFORE saving so the UI and data are in sync
    const ctrl = this.getControl(detail, cov);
    if (checked) {
      const isFixed = this.isCoverageFixedLimit(detail, cov);
      const isFraction = this.hasFraction(cov);
      const validators = [Validators.required];

      if (isFraction) {
        ctrl.setValue(initialAmount, { emitEvent: false });
      } else if (isFixed && range?.maxAmount !== undefined) {
        validators.push(Validators.min(range.maxAmount));
        validators.push(Validators.max(range.maxAmount));
        ctrl.setValue(initialAmount, { emitEvent: false });
      } else {
        if (range?.minAmount !== undefined) validators.push(Validators.min(range.minAmount));
        if (range?.maxAmount !== undefined) validators.push(Validators.max(range.maxAmount));
        ctrl.setValue(initialAmount, { emitEvent: false });
      }

      ctrl.setValidators(validators);
      ctrl.enable({ emitEvent: false });
      ctrl.updateValueAndValidity({ emitEvent: false });
    } else {
      ctrl.clearValidators();
      ctrl.disable({ emitEvent: false });
      ctrl.setErrors(null);
      ctrl.setValue(0, { emitEvent: false });
      ctrl.updateValueAndValidity({ emitEvent: false });
    }

    // Save AFTER the correct amount has been determined and set
    this.onSave(true);
  }

  // Map to store controls: key = `${detailId}_${coverageId}`
  controls = new Map<string, FormControl>();

  getControl(detail: QuoteDetailDto, cov: InsuranceTypeCoverageDto): FormControl {
    const key = `${detail.id}_${cov.id}`;
    if (this.controls.has(key)) {
      const ctrl = this.controls.get(key)!;

      // Age-based constraint: auto-deselect if age exceeds maxAge
      const disabledByAge = this.isCoverageDisabledByAge(detail, cov);
      if (disabledByAge) {
        if (this.isCoverageSelected(detail, cov)) {
          // Remove the coverage from the quote data
          this.onToggleCoverage(detail, cov, false);
        }
        ctrl.disable({ emitEvent: false });
        ctrl.setErrors(null);
        return ctrl;
      }

      // Sync value if changed externally (e.g. from modal)
      const currentVal = this.getCoverageAmount(detail, cov) ?? 0;
      if (ctrl.value !== currentVal && !ctrl.dirty) {
        ctrl.setValue(currentVal, { emitEvent: false });
      }
      // Sync disabled state
      const isSelected = this.isCoverageSelected(detail, cov);
      if (isSelected && ctrl.disabled) ctrl.enable({ emitEvent: false });
      if (!isSelected && ctrl.enabled) {
        ctrl.disable({ emitEvent: false });
        ctrl.setErrors(null);
      }

      // Sync validators (max may have changed if primary amount was edited in modal)
      const range = this.getCoverageRange(detail, cov);
      const isFixed = this.isCoverageFixedLimit(detail, cov);
      const isFraction = this.hasFraction(cov);
      const validators = isSelected ? [Validators.required] : [];

      if (isSelected && isFraction) {
        // Fraction-based: auto-compute value, readonly handles user input
        const fraction = cov.insurePrimaryFraction ?? 0;
        const requiredTotal = this.getRequiredCoveragesTotal(detail);
        const computed = Math.round((fraction / 100) * requiredTotal * 100) / 100;
        if (ctrl.value !== computed) {
          ctrl.setValue(computed, { emitEvent: false });
        }
      } else if (isSelected && isFixed && range?.maxAmount !== undefined) {
        validators.push(Validators.min(range.maxAmount));
        validators.push(Validators.max(range.maxAmount));
        if (ctrl.value !== range.maxAmount) {
          ctrl.setValue(range.maxAmount, { emitEvent: false });
        }
      } else {
        if (range?.minAmount !== undefined) validators.push(Validators.min(range.minAmount));
        if (range?.maxAmount !== undefined) validators.push(Validators.max(range.maxAmount));
      }

      ctrl.setValidators(validators);
      ctrl.updateValueAndValidity({ emitEvent: false });

      return ctrl;
    }

    const initialValue = this.getCoverageAmount(detail, cov) ?? 0;
    const isSelected = this.isCoverageSelected(detail, cov);
    const range = this.getCoverageRange(detail, cov);
    const isFixed = this.isCoverageFixedLimit(detail, cov);
    const validators = isSelected ? [Validators.required] : [];

    if (isSelected && isFixed && range?.maxAmount !== undefined) {
      validators.push(Validators.min(range.maxAmount));
      validators.push(Validators.max(range.maxAmount));
    } else {
      if (range?.minAmount !== undefined) validators.push(Validators.min(range.minAmount));
      if (range?.maxAmount !== undefined) validators.push(Validators.max(range.maxAmount));
    }

    // Initialize with disabled state if not selected or if age-disabled
    const disabledByAge = this.isCoverageDisabledByAge(detail, cov);
    const control = new FormControl({ value: initialValue, disabled: !isSelected || disabledByAge }, validators);

    // Subscribe to changes
    control.valueChanges.pipe(debounceTime(800)).subscribe(value => {
      if (control.valid) {
        this.onChangeCoverageAmount(detail, cov, value);
        this.onSave(false);
      }
    });

    this.controls.set(key, control);
    return control;
  }

  getCoverageRange(detail: QuoteDetailDto, cov: InsuranceTypeCoverageDto): { min?: number; max?: number; minAmount?: number; maxAmount?: number } | null {
    const code = (detail.currency as string) || '';
    const covRange = this.findCurrencyRange(cov.currencies, code);

    // For primary/required coverage, fall back to insurance-type currency limits
    const isPrimary = cov.isPrimary || cov.isRequired;
    if (isPrimary) {
      const typeRange = this.findCurrencyRange(detail.insuranceType?.currencies, code);
      const merged: { min?: number; max?: number; minAmount?: number; maxAmount?: number } = {};

      if (covRange?.minAmount != null) merged.minAmount = covRange.minAmount;
      else if (typeRange?.minAmount != null) merged.minAmount = typeRange.minAmount;

      if (covRange?.maxAmount != null) merged.maxAmount = covRange.maxAmount;
      else if (typeRange?.maxAmount != null) merged.maxAmount = typeRange.maxAmount;

      return Object.keys(merged).length ? merged : null;
    }

    // For secondary coverages, cap at primary coverage amount (if not Enfermedades Graves)
    if (!this.isEnfermedadesGraves(detail)) {
      const required = this.getRequiredCoverages(detail);
      // Use the first required coverage as the primary for capping logic
      const primary = required.length > 0 ? required[0] : null;
      if (primary) {
        const primaryAmount = this.getCoverageAmount(detail, primary) ?? 0;
        if (primaryAmount > 0) {
          const range = covRange || {};
          const maxAmount = range.maxAmount != null ? Math.min(range.maxAmount, primaryAmount) : primaryAmount;
          return { ...range, maxAmount };
        }
      }
    }

    return covRange;
  }

  public isCoverageFixedLimit(detail: QuoteDetailDto, cov?: InsuranceTypeCoverageDto): boolean {
    if (!cov || !cov.currencies) return false;
    const code = (detail.currency as string) || '';
    const found = (cov.currencies ?? []).find(
      (c) => (c?.currency || '').toUpperCase() === code.toUpperCase(),
    );
    return found?.isFixedLimit ?? false;
  }

  /** Returns true when the coverage has an insurePrimaryFraction > 0. */
  hasFraction(cov?: InsuranceTypeCoverageDto): boolean {
    return !!cov && typeof cov.insurePrimaryFraction === 'number' && cov.insurePrimaryFraction > 0;
  }

  /** Computes the total amount of required coverages for a given detail. */
  /** Base for percentage-of-sum-assured coverages: required coverages that aren't themselves a percentage. */
  getRequiredCoveragesTotal(detail: QuoteDetailDto): number {
    const required = this.getRequiredCoverages(detail).filter((cov) => !this.hasFraction(cov));
    return required.reduce((sum, cov) => sum + (this.getCoverageAmount(detail, cov) ?? 0), 0);
  }

  private findCurrencyRange(list: any[] | null | undefined, code: string): { min?: number; max?: number; minAmount?: number; maxAmount?: number; isFixedLimit?: boolean } | null {
    const found = (list ?? []).find(
      (c) => (c?.currency || '').toUpperCase() === code.toUpperCase(),
    );
    if (!found) return null;
    const range: any = {};
    if (typeof found.min === 'number') range.min = found.min;
    if (typeof found.max === 'number') range.max = found.max;
    if (typeof found.minAmount === 'number') range.minAmount = found.minAmount;
    if (typeof found.maxAmount === 'number') range.maxAmount = found.maxAmount;
    if (typeof found.isFixedLimit === 'boolean') range.isFixedLimit = found.isFixedLimit;
    return Object.keys(range).length ? range : null;
  }

  onChangeCoverageAmount(
    detail: QuoteDetailDto,
    cov: InsuranceTypeCoverageDto,
    amountOrStr: number | string | null,
  ): void {
    const amount = Number((amountOrStr ?? 0).toString().replace(/,/g, '')) || 0;
    const cur = this.quotes.currentQuote;
    if (!cur || !detail.id) return;
    const details = (cur.details ?? []).map((d) => {
      if (d.id !== detail.id) return d;
      const existing = d.coverages ?? [];
      const idx = existing.findIndex((s) => matchCoverage(s, cov));
      if (idx !== -1) {
        existing[idx] = { ...existing[idx], amount };
      }
      return { ...d, coverages: [...existing] };
    });
    this.quotes.setCurrentQuoteLocal({ ...cur, details });

  }

  onDeleteDetail(detailId?: number): void {
    if (!detailId) return;
    this.quotes.deleteDetailLocal(detailId);
    this.detailsCount = this.quotes.currentQuote?.details?.length ?? 0;
    this.syncExpandedDetailsWithCurrentDetails();
  }

  otherInsuredCount(detail: QuoteDetailDto): number {
    const people = detail.insuredPeople ?? [];
    return people.filter((p) => !p.isPrimary).length;
  }

  detailPlanName(detailId?: number): string {
    if (!detailId || !this.summary?.details) return '';
    const d = this.summary.details.find((x) => x.detailId === detailId);
    return d?.planName ?? '';
  }

  protected readonly getSafeIconName = getSafeIconName;

  onEditDetail(detail: QuoteDetailDto): void {
    if (!detail?.insuranceType) return;
    const modalRef = this.modal.open(OptionModalComponent, {
      size: 'lg',
      centered: true,
    });
    modalRef.componentInstance.option = detail.insuranceType;
    modalRef.componentInstance.selected = true;
    modalRef.componentInstance.canDelete = this.currentDetails.length > 1;
    modalRef.componentInstance.disableQuestions = true;
    modalRef.componentInstance.initialProducerCode =
      normalizeProducerCode(this.quotes.currentQuote?.producerCode);
    modalRef.componentInstance.initialDetail = {
      typeId: detail.typeId!,
      selectedPlanId: detail.selectedPlanId ?? null,
      durationYears: detail.durationYears ?? null,
      currency: detail.currency ?? null,
      coverages: (detail.coverages ?? []).map((c) => ({
        id: c.id ?? null,
        coverageId: c.coverageId ?? c.coverage?.id ?? null,
        coverageCode: c.coverageCode ?? c.coverage?.coverageCode ?? null,
        name: c.name ?? null,
        description: c.description ?? null,
        amount: c.amount ?? 0,
        currency: detail.currency ?? null,
      })),
      insuredPeople: (detail.insuredPeople ?? []).map((p) => ({
        id: p.id ?? null,
        personId: p.personId ?? null,
        isPrimary: p.isPrimary ?? false,
        person: p.person
          ? ({
            id: p.person.id ?? null,
            name: p.person.name ?? null,
            lastName: p.person.lastName ?? null,
            documentType: (p.person as any).documentType ?? null,
            documentNumber: p.person.documentNumber ?? null,
            birthDate: p.person.birthDate ?? null,
            gender: (p.person as any).gender ?? null,
            civilState: (p.person as any).civilState ?? null,
            heightCm: (p.person as any).heightCm ?? null,
            weightLb: (p.person as any).weightLb ?? null,
            nacionality: (p.person as any).nacionality ?? null,
            relationship: (p.person as any).relationship ?? null,
            primaryInsuredRelation:
              (p.person as any).primaryInsuredRelation ?? null,
            occupation: p.person.occupation ?? null,
            personAddressId: (p.person as any).personAddressId ?? null,
            isPep: (p.person as any).isPep ?? null,
            address: undefined,
          } as any)
          : undefined,
      })),
      answers: (detail.answers ?? []).map((a) => ({
        id: a.id ?? null,
        questionId: a.questionId!,
        value: a.value ?? null,
      })),
    } as any;

    // Pass other selected details for currency validation and conversion
    const otherSelectedDetails = (this.quotes.currentQuote?.details ?? [])
      .filter((d) => d.id !== detail.id)
      .map((d) => ({
        detail: d,
        insuranceType: d.insuranceType!,
      }))
      .filter((item) => !!item.insuranceType);
    modalRef.componentInstance.otherSelectedDetails = otherSelectedDetails;

    // Pass current global currency
    if (this.quotes.currentQuote?.details?.length) {
      modalRef.componentInstance.currentGlobalCurrency = this.quotes.currentQuote.details[0].currency;
    }


    modalRef.result
      .then(async (result) => {
        // Result shape from OptionModal: { option, formValue }
        if (!result || !detail.id) return;
        const cur = this.quotes.currentQuote;
        if (!cur) return;
        const option = result.option as any;
        const formValue = result.formValue as any;
        const updated = this.mapModalResultToDetail(detail, option, formValue);

        // Update producerCode based on intermediary selection
        const newProducerCode =
          formValue?.intermediaryEnabled && formValue?.intermediary
            ? String(formValue.intermediary)
            : null;

        // Propagate currency change to all details using returned conversions
        const newCurrency = updated.currency;
        const otherSelectedDetails = (result.otherSelectedDetails ?? []) as any[];
        const updatedIds = new Set(otherSelectedDetails.map((item) => item.detail.id));

        const details = (cur.details ?? []).map((d) => {
          if (d.id === detail.id) return updated;
          if (updatedIds.has(d.id)) {
            const convertedItem = otherSelectedDetails.find((item) => item.detail.id === d.id);
            const convertedDetail = convertedItem.detail;
            return {
              ...d,
              currency: newCurrency,
              coverages: (d.coverages ?? []).map((c: any) => {
                const convertedCov = (convertedDetail.coverages ?? []).find(
                  (cc: any) => (cc.id || cc.coverageCode) === (c.id || c.coverage?.id),
                );
                return {
                  ...c,
                  currency: newCurrency,
                  amount: convertedCov ? convertedCov.amount : c.amount,
                };
              }),
            };
          }
          return d;
        });

        this.quotes.setCurrentQuoteLocal({ ...cur, producerCode: newProducerCode, details });

        // Save and re-apply producerCode after server response (server may not clear it)
        this.saving = true;
        this.swal.showLoading();
        try {
          const resp = await lastValueFrom(this.quotes.saveCurrentQuote());
          // Preserve the user's producerCode choice over the server response
          resp.producerCode = newProducerCode;
          this.quotes.setCurrentQuote(resp);
          const id = resp.quoteId;
          if (id) {
            this.quotes.getQuoteSummary(id, this.paymentsPlan).subscribe({
              next: (s) => (this.summary = s ?? null),
              complete: () => (this.saving = false),
              error: () => (this.saving = false),
            });
          } else {
            this.saving = false;
          }
        } catch (e) {
          this.saving = false;
        }
        this.swal.dismiss();
      })
      .catch((reason) => {
        if (reason === 'remove') {
          this.onDeleteDetail(detail.id);
        }
      });
  }
  private mapModalResultToDetail(
    existing: QuoteDetailDto,
    option: any,
    formValue: any,
  ): QuoteDetailDto {
    const updated: QuoteDetailDto = { ...existing };

    // Currency and duration
    const durationMonths = Number(formValue?.duration ?? 0);
    updated.durationYears = Math.max(0, Math.ceil(durationMonths / 12));
    if (formValue?.currency) {
      updated.currency = formValue.currency;
    }

    // Resolve selectedPlanId
    if (formValue?.planOption) {
      updated.selectedPlanId = resolveSelectedPlanId(option, formValue.planOption);
    }

    // Coverages (primary + additionals + plan-based)
    updated.coverages = mapCoveragesFromForm(option, formValue, existing.coverages ?? []);

    // Insured people (primary / spouse / children)
    updated.insuredPeople = mapInsuredPeopleFromForm(formValue, existing.insuredPeople ?? []);

    // Health questionnaire answers
    const answersOut = mapAnswersFromForm(formValue, existing.answers ?? []);
    if (answersOut.length > 0) {
      updated.answers = answersOut;
    }

    return updated;
  }

  @ViewChildren('infoButton', { read: ElementRef }) infoButtons!: QueryList<
    ElementRef<HTMLButtonElement>
  >;
  additionalCoverageSelection: Record<'coverage1' | 'coverage2', boolean> = {
    coverage1: false,
    coverage2: false,
  };
  private popoverCtor?: PopoverCtor;
  private readonly popovers = new Map<HTMLElement, PopoverInstance>();

  // Summary state
  summary: QuoteSummaryDto | null = null;
  detailsCount = 0;
  saving = false;
  isLoading = true;

  // Payments plan state (number of payments per year). Default 1 (Pago único)
  paymentsPlan: number = 1;
  private quoteId: number | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly quotes: QuotesService,
    private readonly modal: NgbModal,
  ) {
    // Load summary on construction to align with standalone component lifecycle
    this.route.queryParamMap.subscribe((params) => {
      const qid = params.get('quoteId');
      const id = qid
        ? Number(qid)
        : (this.quotes.currentQuote?.quoteId ?? null);
      this.quoteId = id;
      if (id) {
        this.swal.showLoading('Cargando resumen de cotización...');
        this.quotes.getQuoteSummary(id, this.paymentsPlan).subscribe({
          next: (s) => {
            this.summary = s ?? null;
            const countFromSummary = this.summary?.details?.length ?? null;
            if (typeof countFromSummary === 'number')
              this.detailsCount = countFromSummary;
            else
              this.detailsCount =
                this.quotes.currentQuote?.details?.length ?? 0;
            this.syncExpandedDetailsWithCurrentDetails();
            this.isLoading = false;
            this.swal.dismiss();
          },
          error: () => {
            // Fallback to current quote info if available
            this.summary = this.quotes.currentQuote?.summary ?? null;
            this.detailsCount = this.quotes.currentQuote?.details?.length ?? 0;
            this.syncExpandedDetailsWithCurrentDetails();
            this.isLoading = false;
            this.swal.dismiss();
          },
        });
      } else {
        // No id available; fallback to any currentQuote in memory
        this.summary = this.quotes.currentQuote?.summary ?? null;
        this.detailsCount = this.quotes.currentQuote?.details?.length ?? 0;
        this.syncExpandedDetailsWithCurrentDetails();
        this.isLoading = false;
        this.swal.dismiss();
      }
    });
  }

  onAdditionalCoverageToggle(
    key: 'coverage1' | 'coverage2',
    event: Event,
  ): void {
    const target = event.target as HTMLInputElement | null;
    this.additionalCoverageSelection[key] = target?.checked ?? false;
  }

  onInfoClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const button = event.currentTarget as HTMLButtonElement | null;
    button?.focus();
  }

  onPaymentsChange(event: Event): void {
    const value = Number(
      (event.target as HTMLSelectElement | null)?.value || 1,
    );
    this.paymentsPlan = value || 1;
    const id = this.quoteId ?? this.quotes.currentQuote?.quoteId ?? null;
    if (id) {
      this.quotes.getQuoteSummary(id, this.paymentsPlan).subscribe({
        next: (s) => {
          this.summary = s ?? null;
          const countFromSummary = this.summary?.details?.length ?? null;
          if (typeof countFromSummary === 'number')
            this.detailsCount = countFromSummary;
          else
            this.detailsCount = this.quotes.currentQuote?.details?.length ?? 0;
          this.syncExpandedDetailsWithCurrentDetails();
        },
        error: () => {
          // keep old summary on error
        },
      });
    }
  }

  expandedDetails = new Set<number>();

  isExpanded(detailId?: number): boolean {
    return !!detailId && this.expandedDetails.has(detailId);
  }

  toggleExpansion(detailId?: number): void {
    if (!detailId) return;
    if (this.expandedDetails.has(detailId)) {
      this.expandedDetails.delete(detailId);
    } else {
      // Accordion behavior: collapse all others first
      this.expandedDetails.clear();
      this.expandedDetails.add(detailId);
      // Re-check popovers when expanding
      setTimeout(() => void this.ensurePopovers(), 0);
    }
  }

  ngAfterViewInit(): void {
    void this.ensurePopovers();
  }

  ngAfterViewChecked(): void {
    void this.ensurePopovers();
  }

  ngOnDestroy(): void {
    this.detachPopovers();
  }

  private async ensurePopovers(): Promise<void> {
    if (typeof window === 'undefined') {
      return;
    }

    const buttons = this.infoButtons?.toArray() ?? [];
    const elements = new Set<HTMLElement>();

    buttons.forEach((buttonRef) => elements.add(buttonRef.nativeElement));

    if (!elements.size) {
      this.detachPopovers();
      return;
    }

    if (!this.popoverCtor) {
      const { default: Popover } = await import('bootstrap/js/dist/popover.js');
      this.popoverCtor = Popover;
    }

    // Dispose popovers that are no longer present
    this.popovers.forEach((popover, element) => {
      if (!elements.has(element)) {
        popover.dispose();
        this.popovers.delete(element);
      }
    });

    // Attach popovers to new elements
    elements.forEach((element) => {
      if (this.popovers.has(element) || !this.popoverCtor) {
        return;
      }

      const title = element.dataset['infoTitle'] ?? '';
      const content = formatDescription(element.dataset['infoContent'] ?? '');

      const popover = new this.popoverCtor(element, {
        trigger: 'focus',
        title,
        content,
        container: 'body',
        html: true,
        customClass: 'custom-popover',
      });

      this.popovers.set(element, popover);
    });
  }

  private detachPopovers(): void {
    this.popovers.forEach((popover) => popover.dispose());
    this.popovers.clear();
  }

  private syncExpandedDetailsWithCurrentDetails(): void {
    const ids = new Set(
      this.currentDetails
        .map((d) => d?.id)
        .filter((id): id is number => typeof id === 'number'),
    );

    this.expandedDetails.forEach((id) => {
      if (!ids.has(id)) {
        this.expandedDetails.delete(id);
      }
    });

    if (this.expandedDetails.size === 0) {
      const firstId = this.currentDetails[0]?.id;
      if (firstId) {
        this.expandedDetails.add(firstId);
      }
    }
  }

  async onSave(showLoader = true) {
    if (this.hasErrors) {
      this.swal.error('Por favor, corrija los errores en las coberturas antes de guardar. Recuerde que el valor de las coberturas adicionales no puede ser superior al de la cobertura principal.');
      return;
    }

    this.saving = true;
    if (showLoader) this.swal.showLoading();
    try {
      const resp = await lastValueFrom(this.quotes.saveCurrentQuote());

      this.quotes.setCurrentQuote(resp);
      const id = resp.quoteId;
      if (id) {
        this.quotes.getQuoteSummary(id, this.paymentsPlan).subscribe({
          next: (s) => (this.summary = s ?? null),
          complete: () => (this.saving = false),
          error: () => (this.saving = false),
        });
      } else {
        this.saving = false;
      }
    } catch (e) {
      this.saving = false;
    }
    if (showLoader) this.swal.dismiss();
  }

  async onDownloadQuote() {
    if (this.hasErrors) {
      this.swal.error('Por favor, corrija los errores en las coberturas antes de descargar. Recuerde que el valor de las coberturas adicionales no puede ser superior al de la cobertura principal.');
      return;
    }

    const q = this.quotes.currentQuote;
    if (!q?.quoteId) return;

    // Check if user info is already saved (proxy via quoteEmail)
    if (q.quoteEmail) {
      this.swal.showLoading('Descargando PDF...');
      try {
        const blob = await lastValueFrom(this.quotes.downloadQuotePdf(q.quoteId));
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `Cotizacion-${q.quoteId}.pdf`;
        anchor.click();
        window.URL.revokeObjectURL(url);
        this.swal.dismiss();
      } catch (error) {
        this.swal.error('Ocurrió un error al descargar la cotización.');
      }
      return;
    }

    const modalRef = this.modal.open(DownloadQuoteModalComponent, {
      centered: true,
      backdrop: 'static'
    });

    modalRef.componentInstance.quoteId = q.quoteId;
    modalRef.componentInstance.initialEmail = q.quoteEmail || '';

    try {
      const result = await modalRef.result;
      if (result) {
        // Modal handles the download and state update
        this.swal.success('Tu cotización ha sido descargada correctamente.');
      }
    } catch {
      // dismissed
    }
  }

  private isAnyControlInvalid(): boolean {
    let hasInvalid = false;
    this.controls.forEach(ctrl => {
      if (ctrl.invalid && ctrl.enabled) {
        hasInvalid = true;
      }
    });

    // Also check if any secondary coverage exceeds primary in the data itself (sanity check)
    for (const detail of this.currentDetails) {
      if (this.isEnfermedadesGraves(detail)) continue;
      const required = this.getRequiredCoverages(detail);
      const primary = required.length > 0 ? required[0] : null;
      if (!primary) continue;
      const primaryAmount = this.getCoverageAmount(detail, primary) ?? 0;
      if (primaryAmount <= 0) continue;

      const additionals = this.getNonRequiredCoverages(detail);
      for (const cov of additionals) {
        if (!this.isCoverageSelected(detail, cov)) continue;
        const amount = this.getCoverageAmount(detail, cov) ?? 0;
        if (amount > primaryAmount) {
          hasInvalid = true;
        }
      }
    }

    return hasInvalid;
  }
}
